/**
 * V9.11 — Mes articles contributeurs.
 *
 * Liste les articles soumis par l'utilisateur (tous statuts).
 * Permet de modifier/supprimer tant que non publié, et de re-soumettre les rejetés.
 */
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Edit, FileText, Plus, Trash2, Send, Save, Loader2, AlertCircle, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { showError } from "@/lib/errors";
import { MarkdownRenderer } from "@/components/MarkdownRenderer";
import RequireAuth from "@/components/RequireAuth";
import { EmptyState } from "@/components/EmptyState";

const STATUS_LABEL = {
  pending: { label: "En attente de validation", color: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30", Icon: Clock },
  published: { label: "Publié", color: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30", Icon: CheckCircle2 },
  rejected: { label: "Refusé", color: "bg-destructive/15 text-destructive border-destructive/30", Icon: AlertCircle },
};

function StatusBadge({ status }) {
  const s = STATUS_LABEL[status] || STATUS_LABEL.pending;
  const Icon = s.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium ${s.color}`}>
      <Icon className="h-3 w-3" /> {s.label}
    </span>
  );
}

// ----- List page -----
function MineArticlesListInner() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contrib/articles/mine");
      setItems(data.items || []);
    } catch (e) {
      showError(e, { fallback: "Impossible de charger tes articles." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <div className="flex items-center justify-between gap-2 mb-5 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-2xl sm:text-3xl">Mes articles</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Tes contributions au fil <strong>Actualités</strong> — brouillons, en attente, publiés, refusés.
          </p>
        </div>
        <Link to="/contribuer">
          <Button className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="new-article-btn">
            <Plus className="h-4 w-4 mr-1.5" />
            Nouvel article
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="text-center text-muted-foreground py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="Aucun article pour l'instant."
          description="Tu peux contribuer en partageant un récit, un témoignage ou un résultat."
          action={<Link to="/contribuer"><Button className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"><Plus className="h-4 w-4 mr-1.5" />Écrire mon premier article</Button></Link>}
        />
      ) : (
        <ul className="space-y-3" data-testid="mine-articles-list">
          {items.map((a) => (
            <li key={a.id}>
              <Card className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3 min-w-0">
                  {a.cover_url ? (
                    <img src={a.cover_url} alt="" className="h-16 w-20 sm:h-20 sm:w-28 object-cover rounded shrink-0 bg-muted" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                  ) : (
                    <div className="h-16 w-20 sm:h-20 sm:w-28 rounded bg-muted shrink-0 flex items-center justify-center text-muted-foreground"><FileText className="h-6 w-6" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <h3 className="font-display font-semibold leading-tight break-words flex-1 min-w-0">
                        <Link to={`/mes-articles/${a.id}`} className="hover:underline">{a.title || "Sans titre"}</Link>
                      </h3>
                      <StatusBadge status={a.status} />
                    </div>
                    {a.summary && <p className="text-xs text-muted-foreground mt-1 line-clamp-2 break-words">{a.summary}</p>}
                    {a.rejected_reason && a.status === "rejected" && (
                      <div className="text-[11px] text-destructive mt-1 break-words">Refus&nbsp;: {a.rejected_reason}</div>
                    )}
                    <div className="text-[10px] text-muted-foreground mt-2">
                      Créé {a.created_at ? new Date(a.created_at).toLocaleDateString("fr-FR") : "—"}
                      {a.published_at && <> · Publié {new Date(a.published_at).toLocaleDateString("fr-FR")}</>}
                    </div>
                  </div>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ----- Edit page -----
function MineArticleEditInner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/contrib/articles/${id}`);
        setA(data);
        setForm({
          title: data.title || "",
          summary: data.summary || "",
          body_md: data.body_md || "",
          cover_url: data.cover_url || "",
          discipline: data.discipline || "",
        });
      } catch (e) {
        showError(e, { fallback: "Article introuvable." });
        navigate("/mes-articles");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  if (loading || !a || !form) return <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));
  const canEdit = a.status !== "published";

  const save = async ({ submit }) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/contrib/articles/${id}`, { ...form, submit });
      toast.success(submit ? "Article soumis à modération." : "Modifications enregistrées.");
      setA(data);
    } catch (e) {
      showError(e, { fallback: "Impossible d'enregistrer." });
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`/contrib/articles/${id}`);
      toast.success("Article supprimé.");
      navigate("/mes-articles");
    } catch (e) {
      showError(e, { fallback: "Impossible de supprimer." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div>
          <Link to="/mes-articles" className="inline-flex items-center text-xs text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-3.5 w-3.5 mr-1" />
            Mes articles
          </Link>
          <h1 className="font-display font-bold text-2xl sm:text-3xl break-words mt-1">{a.title}</h1>
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <StatusBadge status={a.status} />
            {a.published_at && <span className="text-[11px] text-muted-foreground">Publié le {new Date(a.published_at).toLocaleDateString("fr-FR")}</span>}
            {a.slug && a.status === "published" && (
              <Link to={`/actualites/contrib-${a.slug}`} className="text-[11px] text-[hsl(var(--aito-lagoon))] hover:underline">
                Voir la version publique →
              </Link>
            )}
          </div>
        </div>
        {!canEdit && (
          <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30">
            Article publié — modifications réservées à l'admin
          </Badge>
        )}
      </div>

      {a.status === "rejected" && a.rejected_reason && (
        <Alert variant="destructive" className="mb-5">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Article refusé</AlertTitle>
          <AlertDescription>{a.rejected_reason}<br />
            Tu peux corriger et resoumettre.
          </AlertDescription>
        </Alert>
      )}

      <Card className="p-4 sm:p-5 space-y-4">
        <div>
          <label className="text-xs font-medium">Titre</label>
          <Input data-testid="edit-title-input" value={form.title} disabled={!canEdit} maxLength={180} onChange={(e) => upd({ title: e.target.value })} />
        </div>
        <div>
          <label className="text-xs font-medium">Résumé</label>
          <Textarea value={form.summary} disabled={!canEdit} maxLength={280} rows={2} onChange={(e) => upd({ summary: e.target.value })} />
        </div>
        <div>
          <label className="text-xs font-medium">URL image de couverture</label>
          <Input value={form.cover_url} disabled={!canEdit} onChange={(e) => upd({ cover_url: e.target.value })} placeholder="https://exemple.com/photo.jpg" />
        </div>
        <div>
          <label className="text-xs font-medium">Discipline</label>
          <Input value={form.discipline} disabled={!canEdit} onChange={(e) => upd({ discipline: e.target.value })} />
        </div>
        <div>
          <label className="text-xs font-medium">Contenu (Markdown léger)</label>
          <Textarea value={form.body_md} disabled={!canEdit} maxLength={30000} rows={16} className="font-mono text-xs" onChange={(e) => upd({ body_md: e.target.value })} />
        </div>

        <div className="flex items-center gap-2 pt-2 flex-wrap">
          {canEdit && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="text-destructive border-destructive/40 hover:bg-destructive/5" disabled={busy} data-testid="delete-article-btn">
                  <Trash2 className="h-4 w-4 mr-1.5" /> Supprimer
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Supprimer cet article ?</AlertDialogTitle>
                  <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction onClick={del} className="bg-destructive text-destructive-foreground">Supprimer</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <div className="flex-1" />
          {canEdit && (
            <>
              <Button variant="outline" onClick={() => save({ submit: false })} disabled={busy} data-testid="save-edit-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
                Enregistrer
              </Button>
              <Button onClick={() => save({ submit: true })} disabled={busy} className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="resubmit-article-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
                {a.status === "rejected" ? "Resoumettre" : "Soumettre à validation"}
              </Button>
            </>
          )}
        </div>
      </Card>

      <Card className="p-4 sm:p-5 mt-5">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Aperçu</div>
        {form.cover_url && (
          <img src={form.cover_url} alt={form.title} className="w-full max-h-64 object-cover rounded-md mb-3" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        )}
        <h1 className="font-display font-bold text-2xl mb-1 break-words">{form.title}</h1>
        {form.summary && <p className="text-sm text-muted-foreground italic mb-3">{form.summary}</p>}
        <MarkdownRenderer>{form.body_md || "_Aucun contenu._"}</MarkdownRenderer>
      </Card>
    </div>
  );
}

export default function MineArticlesPage() {
  const { id } = useParams();
  return (
    <RequireAuth>
      {id ? <MineArticleEditInner /> : <MineArticlesListInner />}
    </RequireAuth>
  );
}
